import { notification, Tooltip } from "antd";
import copy from "copy-to-clipboard";
import { diff_match_patch } from "diff-match-patch";
import DOMPurify from "dompurify";
import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../api";
import { renderMessage } from "../components/chat/renderMessage";
import { conversationType, MESSAGE_STATUS } from "../config/Constant";
import { sortConversationList } from "./ConversationHelper";
import "./style.scss";

const fileStore =
  process.env.REACT_APP_FILE_STORE || "http://localhost:8402/media";

const verifiedAccessToken = () => {
  const accessToken = Cookies.get("access_token");
  return !!accessToken;
};

const verifiedRefreshToken = () => {
  const refreshToken = Cookies.get("refresh_token");
  return !!refreshToken;
};

const truncateText = (text, maxLength) => {
  if (!text) return "";

  const isUpperCase = text === text?.toUpperCase();

  /* convert mention into "@ + text" */
  const mentionPattern = /@\[([^\]]+)\]\(([^)]+)\)/g;

  text = text.replace(mentionPattern, (match, text) => {
    return "@" + text;
  });

  if (isUpperCase || (!text?.includes(" ") && text?.length > maxLength)) {
    maxLength = maxLength - 5;
  }

  if (text?.length <= maxLength) {
    return renderMessage(text, 18);
  }

  let truncated = text?.substring(0, maxLength);
  const lastSpace = truncated?.lastIndexOf(" ");

  if (lastSpace > 0) {
    truncated = truncated?.substring(0, lastSpace);
  }

  return (
    <>
      {renderMessage(truncated, 18)}
      {" ..."}
    </>
  );
};

const showName = (shortName, maxLength = 30) => {
  const shortNameLength = shortName?.length;

  if (shortNameLength > maxLength) {
    return (
      <Tooltip placement="top" title={shortName}>
        {shortName.substr(0, maxLength) + "..."}
      </Tooltip>
    );
  } else {
    return shortName;
  }
};

export const ShowLinkPreview = ({ previewLink }) => {
  const contentPreviewLink = (content) => {
    const spaceIndices = [];

    for (let i = 0; i < content?.length; i++) {
      if (content[i] === " ") spaceIndices?.push(i);
    }

    const startIndex = spaceIndices?.[9];
    const endIndex = spaceIndices?.[spaceIndices?.length - 6];

    let cutContent;

    if (startIndex < endIndex) {
      cutContent =
        content.slice(0, startIndex) +
        " ... " +
        content.slice(endIndex, spaceIndices?.[spaceIndices?.length - 1]);
    } else cutContent = content;

    return cutContent || "";
  };

  return (
    <div
      className="max-w-[400px] flex flex-col mb-[14px]"
    // key={index}
    // style={{ maxWidth: containerWidth }}
    >
      <a href={previewLink?.url} target="_blank" rel="noopener noreferrer">
        <div className="flex justify-center">
          <img
            src={previewLink?.image}
            alt="preview"
            className="w-[400px] h-[200px] object-cover transform [transform:translateZ(0.1px)] rounded-[5px]"
          />
        </div>
      </a>

      {previewLink?.title && (
        <div className="mt-[8px] ">
          <span className="text-[#26384f] text-sm font-medium leading-relaxed">
            {previewLink?.title}
          </span>
        </div>
      )}
      {previewLink?.description ?? (
        <div className="text-[#9aa9be] mt-[6px] break-all">
          {contentPreviewLink(previewLink?.description || "")}
        </div>
      )}
    </div>
  );
};

const countUrl = (content) => {
  let urlRegex = /(https?:\/\/[^\s<>]+)/g;
  let parts = content?.split(urlRegex);
  return parts?.reduce((accumulator, part) => {
    if (part?.match(urlRegex)) {
      accumulator += 1;
    }

    return accumulator;
  }, 0);
};

const extractUserIds = (content) => {
  const mentionRegex = /@\[[^\]]+\]\(([^)]+)\)/g;
  const userIds = [];
  let match;

  while ((match = mentionRegex.exec(content)) !== null) {
    userIds.push(match[1]);
  }

  return userIds;
};

const removeAccents = (str) => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

function getColorFromInitial(name) {
  const hangulInitials = [
    "ㄱ",
    "ㄲ",
    "ㄴ",
    "ㄷ",
    "ㄸ",
    "ㄹ",
    "ㅁ",
    "ㅂ",
    "ㅃ",
    "ㅅ",
    "ㅆ",
    "ㅇ",
    "ㅈ",
    "ㅉ",
    "ㅊ",
    "ㅋ",
    "ㅌ",
    "ㅍ",
    "ㅎ",
  ];

  const colors = {
    // Korean initials
    ㄱ: "#FF8C00",
    ㄲ: "#FF8C00",
    ㄴ: "#FF8C00",
    ㄷ: "#FF8C00",
    ㄸ: "#FF8C00",
    ㄹ: "#FF8C00",
    ㅁ: "#408fff",
    ㅂ: "#408fff",
    ㅃ: "#408fff",
    ㅅ: "#408fff",
    ㅆ: "#408fff",
    ㅇ: "#408fff",
    ㅈ: "#3CB371",
    ㅉ: "#3CB371",
    ㅊ: "#3CB371",
    ㅋ: "#3CB371",
    ㅌ: "#3CB371",
    ㅍ: "#3CB371",
    ㅎ: "#3CB371",

    // Vietnamese letters
    A: "#FF8C00",
    Ă: "#FF8C00",
    Â: "#FF8C00",
    B: "#FF8C00",
    C: "#FF8C00",
    D: "#FF8C00",
    Đ: "#FF8C00",
    E: "#FF8C00",
    Ê: "#FF8C00",
    G: "#FF8C00",
    H: "#3CB371",
    I: "#3CB371",
    K: "#3CB371",
    L: "#3CB371",
    M: "#3CB371",
    N: "#3CB371",
    O: "#3CB371",
    Ô: "#3CB371",
    Ơ: "#3CB371",
    P: "#3CB371",
    Q: "#408fff",
    R: "#408fff",
    S: "#408fff",
    T: "#408fff",
    U: "#408fff",
    Ư: "#408fff",
    V: "#408fff",
    X: "#408fff",
    Y: "#408fff",
    W: "#408fff",
    Z: "#408fff",
  };

  if (!name || typeof name !== "string") return "#CCCCCC";

  const firstChar = name.trim()[0];

  const code = firstChar.charCodeAt(0);

  // Check if it's Hangul syllable
  if (code >= 0xac00 && code <= 0xd7a3) {
    const initialIndex = Math.floor((code - 0xac00) / (21 * 28));
    const initial = hangulInitials[initialIndex];
    return colors[initial] || "#CCCCCC";
  }

  // If not Hangul → treat as Vietnamese/Latin
  const upperChar = firstChar.toUpperCase();
  return colors[upperChar] || "#CCCCCC";
}

function getColor(initial) {
  const colors = {
    ㄱ: "#E74C3C",
    ㄴ: "#3498DB",
    ㄷ: "#2ECC71",
    ㄹ: "#9B59B6",
    ㅁ: "#E67E22",
    ㅂ: "#F1C40F",
    ㅅ: "#1ABC9C",
    ㅇ: "#E74C3C",
    ㅈ: "#3498DB",
    ㅊ: "#2ECC71",
    ㅋ: "#9B59B6",
    ㅌ: "#E67E22",
    ㅍ: "#F1C40F",
    ㅎ: "#1ABC9C",
  };
  return colors[initial?.toUpperCase()] || "#000000";
}

export const checkImageURL = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

export const getBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const getAvatar = (user) => {
  if (!user?.avatar) return null;
  const url = fileStore + "/user/" + user.userId + user.avatar;
  // +
  // "?token=" +
  // Cookies.get("access_token");

  return url;
};

const getGroupAvatar = async (avatar, conversationId, setAvatarUrl) => {
  if (!avatar || !conversationId) {
    setAvatarUrl(null);
    return;
  }

  const url =
    fileStore +
    "/conversation/" +
    conversationId +
    avatar +
    "?token=" +
    Cookies.get("access_token");
  const isImage = await checkImageURL(url);

  if (isImage) {
    setAvatarUrl(url);
    return;
  } else {
    setAvatarUrl(null);
    return;
  }
};

const getGroupAvatarUrl = (avatar, conversationId) => {
  if (!avatar || !conversationId) return null;

  const url =
    fileStore +
    "/conversation/" +
    conversationId +
    avatar +
    "?token=" +
    Cookies.get("access_token");

  return url;
};

const getUserRole = (projectCode, participantCode, projectParticipant) => {
  const participant = projectParticipant.find(
    (pj) =>
      pj.projectCode === projectCode && pj.participantCode === participantCode,
  );
  return participant ? participant : null;
};

const getAuthority = (authority, userCode, projectParticipant) => {
  const sel = authority.queryCode;
  const inp = authority.inputCode;
  const upd = authority.modificationCode;
  const del = authority.deletionCode;
  const userProject = getUserRole(
    authority.projectCode,
    userCode,
    projectParticipant,
  );

  if (!userProject) return "____";

  return (
    (userProject.authorityCode <= inp ? "C" : "_") +
    (userProject.authorityCode <= sel ? "R" : "_") +
    (userProject.authorityCode <= upd ? "U" : "_") +
    (userProject.authorityCode <= del ? "D" : "_")
  );
};

function getUserPermissions(
  projectCode,
  authorityId,
  userCode,
  projectAuthority,
  projectParticipant,
) {
  let permission;

  projectCode = parseInt(projectCode);

  const authority = projectAuthority.find(
    (authority) =>
      authority.projectCode === projectCode &&
      authority.authorityId === authorityId,
  );
  permission = getAuthority(authority, userCode, projectParticipant);
  permission =
    permission +
    (permission.includes("C") ||
      permission.includes("R") ||
      permission.includes("U") ||
      permission.includes("D")
      ? "V"
      : "_");

  return permission;
}

const standardizeVolume = (byteVolume) => {
  if (byteVolume === 1) return "1 BYTE";
  if (byteVolume < 1024) return byteVolume + " BYTES";
  if (byteVolume === 1024) return "1 KB";
  if (byteVolume < 1024 * 1024) return (byteVolume / 1024).toFixed(2) + " KBS";
  if (byteVolume === 1024 * 1024) return "1 MB";
  if (byteVolume < 1024 * 1024 * 1024)
    return (byteVolume / 1024 / 1024).toFixed(2) + " MBS";
  if (byteVolume === 1024 * 1024 * 1024) return "1 GB";
  return (byteVolume / 1024 / 1024 / 1024).toFixed(2) + " GBS";
};

const imageTypes = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "jfif"];
const audioTypes = ["mp3", "wav", "ogg", "flac"];
const videoTypes = [
  "mp4",
  "avi",
  "mkv",
  "webm",
  "quicktime",
  "x-flv",
  "x-ms-wmv",
  "mov",
  "3g2",
  "3gp",
  "aaf",
  "asf",
  "avchd",
  "avi",
  "drc",
  "flv",
  "m2v",
  "m3u8",
  "m4p",
  "m4v",
  "mkv",
  "mng",
  "mov",
  "mp2",
  "mp4",
  "mpe",
  "mpeg",
  "mpg",
  "mpv",
  "mxf",
  "nsv",
  "ogg",
  "ogv",
  "qt",
  "rm",
  "rmvb",
  "roq",
  "svi",
  "vob",
  "webm",
  "wmv",
  "yuv",
];

const isValidUrl = (url) => {
  const regex = /^(http|https):\/\/[^ "]+$/;
  return regex.test(url);
};

const isTrueUrl = (url) => {
  const start = url.indexOf("/store/");
  const end = url.indexOf("?");
  const isValid = start !== -1 && (end === -1 || end > start);

  return isValid;
};

const isBase64 = (str) => {
  return str.startsWith("data:image/") && str.includes(";base64");
};

/** to extract text and image sources from HTML */
const extractContent = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const text = doc.body.textContent || "";

  const images = Array.from(doc.body.querySelectorAll("img")).map(
    (img) => img.src,
  );

  return { doc, text, images };
};

const compareEmailHTMLContent = (html1, html2) => {
  const parser = new DOMParser();

  const doc1 = parser.parseFromString(html1, "text/html");
  const doc2 = parser.parseFromString(html2, "text/html");

  const text1 = doc1.body.textContent.trim();
  const text2 = doc2.body.textContent.trim();

  const images1 = Array.from(doc1.body.querySelectorAll("img")).map((img) =>
    img.src.trim(),
  );
  const images2 = Array.from(doc2.body.querySelectorAll("img")).map((img) =>
    img.src.trim(),
  );

  const isTextEqual = text1 === text2;

  const isImagesEqual =
    images1.length === images2.length &&
    images1.every((src, index) => src === images2[index]);

  return isTextEqual && isImagesEqual;
};

const checkImageSource = (src) => {
  if (isValidUrl(src)) return "URL";
  if (isBase64(src)) return "BASE64";
  if (isTrueUrl(src)) return "VALID";
  return "INVALID";
};

const convertTextHtmlEditor = async (content, notificationId) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    const imgTags = Array.from(doc.querySelectorAll("img"));

    const imagesWithPositions = imgTags
      .map((img) => {
        const src = img.src;
        const fileExtension = src.match(/data:image\/(.*?);/)?.[1] || "jpeg";
        const imageType = checkImageSource(src);

        if (imageType === "INVALID" || imageType === "URL") {
          return null;
        }

        if (imageType === "BASE64") {
          return { src, fileExtension };
        }

        return null;
      })
      .filter(Boolean);

    if (imagesWithPositions.length > 0) {
      const photoRequests = imagesWithPositions.map(
        ({ src, fileExtension }) => {
          const uid = uuidv4();
          return {
            base64: src,
            fileName: `${uid}.${fileExtension}`,
            conversationId: notificationId,
            contentType: MESSAGE_STATUS.IMAGE,
            folder: conversationType.NOTIFICATION,
          };
        },
      );

      imgTags
        ?.filter((img) => isBase64(img.src))
        .forEach((img, index) => {
          if (photoRequests[index]) {
            img.setAttribute("data-file-name", photoRequests[index].fileName);
          }
        });

      const savedImageRes =
        await apiFactory.resourceApi.storeFile(photoRequests);

      if (savedImageRes?.status === 200) {
        const savedImages = savedImageRes?.data;

        savedImages?.forEach((img) => {
          const savedImgDoc = imgTags?.find((imgDoc) => {
            return img?.fileName?.includes(
              imgDoc.getAttribute("data-file-name"),
            );
          });

          if (savedImgDoc) {
            savedImgDoc.src = img?.path;
            savedImgDoc.removeAttribute("data-file-name");
          }
        });
      } else {
        openNotificationError("Failed to save images.");
      }
    }

    imgTags.forEach((imgDoc) => {
      const start = imgDoc.src.indexOf("/store/");
      const end = imgDoc.src.indexOf("?");
      imgDoc.src =
        end !== -1 ? imgDoc.src.slice(start, end) : imgDoc.src.slice(start);
    });

    return doc.body.innerHTML;
  } catch (error) {
    return <></>;
  }
};
export const getPlainTextFromHtml = (htmlString) => {
  if (!htmlString || typeof htmlString !== "string") {
    return "";
  }

  const cleanHtml = DOMPurify.sanitize(htmlString, {
    USE_PROFILES: { html: true },
  });

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = cleanHtml;

  tempDiv.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));

  tempDiv.querySelectorAll("td, th").forEach((cell) => cell.append(" "));

  tempDiv
    .querySelectorAll(
      "p, div, h1, h2, h3, h4, h5, h6, ul, ol, li, table, blockquote",
    )
    .forEach((block) => {
      block.append("\n\n");
    });

  let plainText = tempDiv.textContent || tempDiv.innerText || "";

  return plainText.replace(/\s+/g, " ").trim();
};

export const getFirstLineFromHtml = (html) => {
  if (!html) return "";
  const firstLineHtml = html.split(/<br\s*\/?>|<\/p>|<\/div>/i)[0];
  const plainText = getPlainTextFromHtml(firstLineHtml).trim();
  return plainText.slice(0, 120);
};

export const convertImageHtmlEditor = (content) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    const imgTags = Array.from(doc.querySelectorAll("img"));

    imgTags.forEach((imgDoc) => {
      const resourceId = imgDoc.getAttribute("resource_id");

      if (resourceId) {
        imgDoc.setAttribute("resource_id", resourceId);
      }
    });

    return doc.body.innerHTML;
  } catch (error) {
    return <></>;
  }
};

export const extractEditorResourceIds = (content) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    const imgTags = Array.from(doc.querySelectorAll("img"));

    const imageResourceId = imgTags.map((imgDoc) => {
      if (!imgDoc?.src) return null;

      const imageType = checkImageSource(imgDoc.src);

      if (imageType === "INVALID") {
        imgDoc.remove();
        return null;
      }

      const parts = imgDoc.src.split("/");
      const resourceId = parts.pop();

      if (!resourceId || resourceId === "null") {
        return null;
      }

      return resourceId;
    });

    const combinedResourceIds = [...imageResourceId.filter((id) => id)];

    const uniqueResourceIds = [...new Set(combinedResourceIds)];

    return uniqueResourceIds;
  } catch (error) {
    return [];
  }
};

const diffHtml = (html1, html2) => {
  const dmp = new diff_match_patch();

  return dmp.diff_main(html1, html2);
};

const processFileUrl = (
  files,
  pathModule = "/conversation/",
  conversationId,
) => {
  return files?.map((file) => ({
    ...file,
    pathUrl: fileStore + pathModule + conversationId + file?.pathUrl,
  }));
};

const convertBlobToFile = (blob, fileName, extraData) => {
  const file = new File([blob], fileName, { type: blob.type });

  return Object.assign(file, extraData);
};

const onChangeUnreadConversation = (
  conversation,
  conversationList,
  handleUpdateConversationList,
) => {
  if (conversation?.unreadCount > 0) {
    const updatedConversationList = conversationList.map((item) =>
      item?.conversationId === conversation?.conversationId
        ? { ...item, unreadCount: 0 }
        : item,
    );

    handleUpdateConversationList(updatedConversationList);

    apiFactory.conversationApi.changeUnread({
      conversationId: conversation?.conversationId,
    });
  }
};

const getFileExtension = (fileName) => {
  if (!fileName || typeof fileName !== "string") return "";
  const lastDotIndex = fileName.lastIndexOf(".");
  return lastDotIndex !== -1
    ? fileName.substring(lastDotIndex + 1).toLowerCase()
    : "";
};

const isSupportedFileType = (fileName) => {
  const fileType = getFileExtension(fileName);

  return [
    "hwp",
    "xls",
    "xlsx",
    "txt",
    "doc",
    "docx",
    "pdf",
    "ppt",
    "pptx",
    // "png",
    // "jpg",
    // "jpeg",
  ]?.includes(fileType);
};

const linkBox = (url) => {
  return `<div class="ck-media__wrapper" data-oembed-url="${url}">
      <div class="ck ck-reset_all ck-media__placeholder">
        <div class="ck-media__placeholder__icon">
          <svg
            class="ck ck-icon ck-reset_all-excluded ck-icon_inherit-color"
            viewBox="0 0 64 42"
            aria-hidden="true"
          >
            <path d="M47.426 17V3.713L63.102 0v19.389h-.001l.001.272c0 1.595-2.032 3.43-4.538 4.098-2.506.668-4.538-.083-4.538-1.678 0-1.594 2.032-3.43 4.538-4.098.914-.244 2.032-.565 2.888-.603V4.516L49.076 7.447v9.556A1.014 1.014 0 0 0 49 17h-1.574zM29.5 17h-8.343a7.073 7.073 0 1 0-4.657 4.06v3.781H3.3a2.803 2.803 0 0 1-2.8-2.804V8.63a2.803 2.803 0 0 1 2.8-2.805h4.082L8.58 2.768A1.994 1.994 0 0 1 10.435 1.5h8.985c.773 0 1.477.448 1.805 1.149l1.488 3.177H26.7c1.546 0 2.8 1.256 2.8 2.805V17zm-11.637 0H17.5a1 1 0 0 0-1 1v.05A4.244 4.244 0 1 1 17.863 17zm29.684 2c.97 0 .953-.048.953.889v20.743c0 .953.016.905-.953.905H19.453c-.97 0-.953.048-.953-.905V19.89c0-.937-.016-.889.97-.889h28.077zm-4.701 19.338V22.183H24.154v16.155h18.692zM20.6 21.375v1.616h1.616v-1.616H20.6zm0 3.231v1.616h1.616v-1.616H20.6zm0 3.231v1.616h1.616v-1.616H20.6zm0 3.231v1.616h1.616v-1.616H20.6zm0 3.231v1.616h1.616v-1.616H20.6zm0 3.231v1.616h1.616V37.53H20.6zm24.233-16.155v1.616h1.615v-1.616h-1.615zm0 3.231v1.616h1.615v-1.616h-1.615zm0 3.231v1.616h1.615v-1.616h-1.615zm0 3.231v1.616h1.615v-1.616h-1.615zm0 3.231v1.616h1.615v-1.616h-1.615zm0 3.231v1.616h1.615V37.53h-1.615zM29.485 25.283a.4.4 0 0 1 .593-.35l9.05 4.977a.4.4 0 0 1 0 .701l-9.05 4.978a.4.4 0 0 1-.593-.35v-9.956z"></path>
          </svg>
        </div>
        <a
          class="ck-media__placeholder__url"
          target="_blank"
          rel="noopener noreferrer"
          href="${url}"
          data-cke-tooltip-text="Open media in new tab"
        >
          <span class="ck-media__placeholder__url__text">${url}</span>
        </a>
      </div>
    </div>`;
};

const mediaBox = (src) => {
  return (
    '<div style="display: flex; justify-content: center;">' +
    '<div style="aspect-ratio: 16 / 9; width: 60%; position: relative;">' +
    `<iframe src="${src}" ` +
    'style="width: 100%; height: 100%; border: 0;" ' +
    'allow="autoplay; encrypted-media" allowfullscreen>' +
    "</iframe>" +
    "</div>" +
    "</div>"
  );
};

const providers = [
  {
    name: "youtube",
    url: [/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/],
    html: (url) => {
      const match = url.match(
        /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/,
      );
      const id = match ? match[1] : null;

      if (!id)
        return `<a target="_blank" rel="noopener noreferrer" href="${url}">${url}</a>`;

      const src = `https://www.youtube.com/embed/${id}`;

      return mediaBox(src);
    },
  },
  {
    name: "vimeo",
    url: [
      /vimeo\.com\/(\d+)/,
      /vimeo\.com\/[^/]+\/[^/]+\/video\/(\d+)/,
      /vimeo\.com\/album\/[^/]+\/video\/(\d+)/,
      /vimeo\.com\/channels\/[^/]+\/(\d+)/,
      /vimeo\.com\/groups\/[^/]+\/videos\/(\d+)/,
      /vimeo\.com\/ondemand\/[^/]+\/(\d+)/,
      /player\.vimeo\.com\/video\/(\d+)/,
    ],
    html: (url) => {
      let id = null;
      const patterns = [
        /vimeo\.com\/(\d+)/,
        /vimeo\.com\/[^/]+\/[^/]+\/video\/(\d+)/,
        /vimeo\.com\/album\/[^/]+\/video\/(\d+)/,
        /vimeo\.com\/channels\/[^/]+\/(\d+)/,
        /vimeo\.com\/groups\/[^/]+\/videos\/(\d+)/,
        /vimeo\.com\/ondemand\/[^/]+\/(\d+)/,
        /player\.vimeo\.com\/video\/(\d+)/,
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
          id = match[1];
          break;
        }
      }

      if (!id)
        return `<a target="_blank" rel="noopener noreferrer" href="${url}">${url}</a>`;

      const src = `https://player.vimeo.com/video/${id}`;

      return mediaBox(src);
    },
  },
  {
    name: "instagram",
    url: [/instagram\.com\/p\/(\w+)/, /instagram\.com\/reel\/(\w+)/],
    html: (url) => {
      return linkBox(url);
    },
  },
  {
    name: "twitter",
    url: [/twitter\.com/, /x\.com/],
    html: (url) => {
      return linkBox(url);
    },
  },
  {
    name: "googleMaps",
    url: [
      /google\.com\/maps/,
      /goo\.gl\/maps/,
      /maps\.google\.com/,
      /maps\.app\.goo\.gl/,
    ],
    html: (url) => {
      return linkBox(url);
    },
  },
  {
    name: "flickr",
    url: /flickr\.com/,
    html: (url) => {
      return linkBox(url);
    },
  },
  {
    name: "facebook",
    url: /facebook\.com/,
    html: (url) => {
      return linkBox(url);
    },
  },
];

export const matchOembed = /<oembed url=["'](.*?)["']\s*><\/oembed>/g;

export const convertOembedToIframe = (html) => {
  return html.replace(matchOembed, (match, url) => {
    for (const provider of providers) {
      const patterns = Array.isArray(provider.url)
        ? provider.url
        : [provider.url];
      for (const pattern of patterns) {
        const result = url.match(pattern);
        if (result) {
          return provider.html ? provider.html(url) : match;
        }
      }
    }
    return match;
  });
};

export const convertToUrl = (html) => {
  const refinedUrlRegex =
    /(?<!["'])((https?|ftp):\/\/(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<>"'()[\]]*[^\s<>"'()[\].,;:])?)/gi;

  return html.replace(refinedUrlRegex, (match, url) => {
    const className = "external-link";

    let actualUrl = url;
    let prefix = "";

    const startMatch = actualUrl.match(/^(\W)(https?:\/\/)/i);
    if (startMatch && startMatch[1] !== "/") {
      prefix = startMatch[1];
      actualUrl = actualUrl.substring(1);
    }

    return `${prefix}<a class="${className}" href="${actualUrl}" target="_blank" rel="noopener noreferrer">${actualUrl}</a>`;
  });
};

const pinnedMessage = async (message, setIsLoadingOnPin = () => { }) => {
  try {
    setIsLoadingOnPin(true);
    const result = await apiFactory.messageApi.pinnedMessage({
      conversationId: message?.conversationId,
      messageId: message?.messageId,
    });
    if (result?.status !== 200) {
      openNotificationError(result?.message);
    }
  } catch (error) {
    openNotificationError(
      "Something wrong! " + error?.response?.data?.message || error.message,
    );
  }
};

const openNotification = (type, message, description) => {
  notification[type]({
    message,
    description,
    placement: "top",
    duration: 6.0,
    showProgress: true,
    pauseOnHover: false,
  });
};

export const openNotificationError = (description) => {
  openNotification("error", "Error", description);
};

export const openNotificationSuccess = (description) => {
  openNotification("success", "Success", description);
};

export const openNotificationWarn = (description) => {
  openNotification("warning", "Warning", description);
};

export const resetSearch = (
  setIsSearching,
  setSearchInputValue,
  setSearchConversationList,
  setSearchName,
) => {
  setIsSearching?.(false);
  setSearchInputValue?.("");
  setSearchConversationList?.([]);
  setSearchName?.({
    searchName: "",
    type: "",
  });
};

const handleCopy = (link) => {
  try {
    const isSuccess = copy(link);

    if (!isSuccess) {
      openNotificationError("Copied to clipboard failed!");
    }
  } catch (error) {
    openNotificationError("Copied to clipboard error! " + error);
  }
};

const sanitizeMentions = (content) =>
  content.replace(/\)\s*@\[|\)@\[/g, ") @[");

const imageExtensions = [
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".bmp",
  ".webp",
  ".svg",
];

function isImageFile(fileName) {
  const ext = getFileExtension(fileName);
  return ext ? imageTypes.includes(ext) : false;
}

function isVideoFile(fileName) {
  const ext = getFileExtension(fileName);
  return ext ? videoTypes.includes(ext) : false;
}

function isAudioFile(fileName) {
  const ext = getFileExtension(fileName);
  return ext ? audioTypes.includes(ext) : false;
}

const highlightText = (text, keyword) => {
  if (!keyword) return text;

  const regex = new RegExp(
    `(${keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  return text.split(regex).map((part, idx) => (
    <span
      key={idx}
      style={{
        fontWeight:
          part.toLowerCase() === keyword.toLowerCase() ? "bold" : "normal",
        color:
          part.toLowerCase() === keyword.toLowerCase() ? "red" : "inherit",
        wordBreak: "break-word",
        whiteSpace: "pre-wrap",
      }}
    >
      {part}
    </span>
  ));
};

export const sortMessageListDesc = (messages = []) => {
  return messages?.sort((a, b) => {
    return new Date(a.createdAt) - new Date(b.createdAt);
  });
};

/* normalize to remove tab, space character*/
const normalizeText = (text) => text.replace(/\s+/g, " ").trim();
const extractText = (text, keyword, maxLength = 50) => {
  if (!text || typeof text !== "string") return "";

  text = normalizeText(text);

  if (!keyword || typeof keyword !== "string") {
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  }

  const lowerText = text.toLowerCase();
  const lowerKeyword = keyword.toLowerCase();
  const index = lowerText.indexOf(lowerKeyword);

  if (index === -1) {
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  }

  const start = Math.max(
    0,
    index - Math.floor((maxLength - keyword.length) / 2),
  );
  const end = start + maxLength;
  const sliced = text.slice(start, end);

  return (start > 0 ? "..." : "") + sliced + (end < text.length ? "..." : "");
};

export {
  audioTypes,
  checkImageSource,
  compareEmailHTMLContent,
  convertBlobToFile,
  convertTextHtmlEditor,
  countUrl,
  diffHtml,
  extractContent,
  extractUserIds,
  getAvatar,
  getColor,
  getColorFromInitial,
  getFileExtension,
  getGroupAvatar,
  getGroupAvatarUrl,
  getUserPermissions,
  handleCopy,
  isAudioFile,
  isBase64,
  isImageFile,
  isSupportedFileType,
  isTrueUrl,
  isValidUrl,
  isVideoFile,
  onChangeUnreadConversation,
  pinnedMessage,
  processFileUrl,
  removeAccents,
  sanitizeMentions,
  showName,
  standardizeVolume,
  truncateText,
  verifiedAccessToken,
  verifiedRefreshToken,
  videoTypes,
  highlightText,
  extractText,
};
